/**
 * Generic-domain lookup prompt (Onboarding #8).
 *
 * Shown as Step 1 when the user registered with a free-email domain
 * (gmail/yahoo/…) so we have no company domain to enrich from. Asks for a
 * company name OR website, resolves it via `/fallback-resolve`, then hands the
 * enriched payload back to the wizard which re-renders the standard Confirm
 * Company form. The user can always skip straight to manual entry.
 */
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';
import { AIProcessingBanner } from '../../../shared/components/common';

interface CompanyLookupPromptProps {
  /** Resolve the query into a company prefill. Rejects when nothing matched. */
  onResolve: (query: string) => Promise<void>;
  /** Drop to manual entry — `typedName` seeds the name field unless it was a URL. */
  onManual: (typedName: string) => void;
}

/** Loose check: does this look like a URL/domain rather than a company name? */
function looksLikeUrl(value: string): boolean {
  const v = value.trim().toLowerCase();
  if (v.includes('://')) return true;
  return !v.includes(' ') && /^[a-z0-9][a-z0-9\-.]*\.[a-z]{2,}/.test(v);
}

export function CompanyLookupPrompt({ onResolve, onManual }: CompanyLookupPromptProps) {
  const { t } = useTranslation(['user_management', 'common']);
  const [query, setQuery] = useState('');
  const [resolving, setResolving] = useState(false);

  const trimmed = query.trim();

  const submit = useCallback(async () => {
    const q = query.trim();
    if (!q || resolving) return;
    setResolving(true);
    try {
      await onResolve(q);
    } catch {
      // No homepage found / fetch failed → manual entry, keeping the typed name.
      onManual(looksLikeUrl(q) ? '' : q);
    } finally {
      setResolving(false);
    }
  }, [query, resolving, onResolve, onManual]);

  if (resolving) {
    return <AIProcessingBanner message={t('setupWizardV2.lookup.resolving', { query: trimmed })} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center gap-3 text-center">
        <span
          className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"
          aria-hidden="true"
        >
          <Search className="h-6 w-6" />
        </span>
        <p className="max-w-sm text-sm text-muted-foreground">
          {t('setupWizardV2.lookup.subheading')}
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="v2-company-lookup">{t('setupWizardV2.lookup.label')}</Label>
        <Input
          id="v2-company-lookup"
          className="h-11"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={t('setupWizardV2.lookup.placeholder')}
          autoComplete="organization"
          autoFocus
        />
      </div>

      <div className="flex flex-col gap-3">
        <Button
          className="h-11 w-full"
          onClick={submit}
          disabled={!trimmed}
          data-testid="company-lookup-submit"
        >
          {t('setupWizardV2.lookup.cta')}
        </Button>
        <Button
          variant="ghost"
          className="h-11 w-full text-muted-foreground"
          onClick={() => onManual('')}
        >
          {t('setupWizardV2.lookup.manual')}
        </Button>
      </div>
    </div>
  );
}
